import { useMemo, useState } from "react";
import MasterComponent from "../../../components/Forms";
import { ComboConfig, MasterConfigs } from "../../../interfaces/entity";
import SmallProfileCard from "../../../components/ContactInfo/SmallProfileCard";
import Badge from "../../../components/Badge";
import moment from "moment";
import { formatPriceIndian } from "../../../constants/functions";
import { Typography } from "antd";
const { Title } = Typography;

const Requirements = () => {
    const [account, setAccount] = useState<string>('');

    const config: MasterConfigs = useMemo(() => ({
        api: () => '/vendor/requirements',
        title: 'Requirements for Vendors',
        modalTitle: (action) => `${action} Vendor Requirements`,
        isActionTab: true,
        onDataLoad: (data) => {
            if (data.account) {
                setAccount(data.account?._id || '');
            }
        },
        getFormColumns: () => [
            {
                name: 'jobTitle',
                label: 'Job Title',
                type: 'select',
                isCombo: true,
                selectProps: {
                    showSearch: true,
                    allowClear: true,
                },
                rules: [{ required: true, message: 'Job Title is required' }],
            },
            {
                name: 'location',
                label: 'Location',
                type: 'select',
                children: ['Onsite', 'Remote', 'Hybrid'].map(item => ({ name: item, value: item })),
                rules: [{ required: true, message: 'Location is required' }],
            },
            {
                name: 'expiry',
                label: 'Expiry Date',
                type: 'date',
            },
            {
                name: 'account',
                label: 'Account',
                type: 'select',
                selectProps: {
                    onChange: (value) => setAccount(value),
                    showSearch: true,
                    allowClear: true,
                },
                isCombo: true,
                rules: [{ required: true, message: 'Account is required' }]
            },
            {
                name: 'contact',
                label: 'Contact',
                type: 'select',
                selectProps: {
                    showSearch: true,
                    allowClear: true,
                },
                isCombo: true,
                rules: [{ required: true, message: 'Contact is required' }]
            },
            {
                name: 'jobStatus',
                label: 'Job Status',
                type: 'select',
                children: ['Open', 'Closed', 'On Hold'].map(item => ({ name: item, value: item })),
            },
            {
                type: 'custom',
                formItem: (e) => <Title level={5} style={{ marginTop: 16 }}>Experience & Budget</Title>,
                span: 24
            },
            {
                name: 'experience',
                label: 'Experience',
                type: 'text',
                rules: [{ required: true, message: 'Experience is required' }],
            },
            {
                name: 'clientBudget',
                label: 'Client Budget',
                type: 'text',
                rules: [{ required: true, message: 'Client Budget is required' }],
            },
            {
                name: 'vendorBudget',
                label: 'Vendor Budget',
                type: 'text'
            },
            {
                type: 'custom',
                formItem: (e) => <Title level={5} style={{ marginTop: 16 }}>Job Details</Title>,
                span: 24
            },
            {
                name: 'jd',
                span: 24,
                label: 'Job Description',
                type: 'quill',
                rules: [{ required: true, message: 'Job Description is required' }],
            }
        ],
        getColumns: () => [
            {
                title: 'Job Title',
                dataIndex: 'jobTitle',
                key: 'jobTitle',
                render: (text: any) => text?.name || '',
                fixed: 'left',
                width: 200
            },
            {
                title: 'Account',
                dataIndex: 'account',
                key: 'account',
                fixed: 'left',
                width: 200,
                render: (text: any) => text?.businessName || ''
            },
            {
                title: 'Contact',
                dataIndex: 'contact',
                key: 'contact',
                fixed: 'left',
                width: 200,
                render: (text: any) => <SmallProfileCard
                    name={[text?.preName, text?.firstName, text?.lastName].filter(Boolean).join(' ')}
                    subTitle={text?.phone || text?.email || ''}
                    avatar={text?.profilePhoto || ''}
                />
            },
            {
                title: 'Location',
                dataIndex: 'location',
                key: 'location',
                width: 150,
            },
            {
                title: 'Experience',
                dataIndex: 'experience',
                key: 'experience',
                width: 150,
                render: (text: string) => text ? `${text}+ Years` : ''
            },
            {
                title: 'Vendor Budget',
                dataIndex: 'vendorBudget',
                key: 'vendorBudget',
                width: 150,
                render: (text: string) => text ? `INR ${formatPriceIndian(text)}` : ''
            },
            {
                title: 'Client Budget',
                dataIndex: 'clientBudget',
                key: 'clientBudget',
                width: 150,
                render: (text: string) => text ? `INR ${formatPriceIndian(text)}` : ''
            },
            {
                title: 'Job Status',
                dataIndex: 'jobStatus',
                key: 'jobStatus',
                align: 'center',
                width: 150,
                render: (text: string) => {
                    if (!text) return '';
                    const color = text === 'Open' ? 'green' : text === 'Closed' ? 'red' : text === 'On Hold' ? 'orange' : 'blue';
                    return <Badge color={color} text={text} />;
                }
            },
            {
                title: 'Expiry',
                dataIndex: 'expiry',
                key: 'expiry',
                align: 'center',
                width: 150,
                render: (text: string, record: any) => {
                    if (!text) return '';
                    const isExpired = record?.jobStatus == 'Closed' ? true : moment().isAfter(moment(text));
                    return <Badge color={isExpired ? 'red' : 'green'} text={isExpired ? 'Expired' : moment(text).fromNow()} />;
                }
            },
            {
                title: 'Requirement Owner',
                dataIndex: 'requirementOwner',
                key: 'requirementOwner',
                width: 200,
                render: (text: any) => <SmallProfileCard
                    name={[text?.firstName, text?.lastName].filter(Boolean).join(' ')}
                    subTitle={text?.employeeId || ''}
                    avatar={text?.avatar || ''}
                />
            },
            {
                title: 'Created At',
                dataIndex: 'createdAt',
                key: 'createdAt',
                width: 150,
                render: (text: string) => moment(text).format('DD MMM, YYYY hh:mm A')
            },
            {
                title: 'Updated By',
                dataIndex: 'updatedBy',
                key: 'updatedBy',
                width: 200,
                render: (text: any) => text ? <SmallProfileCard
                    name={[text?.firstName, text?.lastName].filter(Boolean).join(' ')}
                    subTitle={text?.employeeId || ''}
                    avatar={text?.avatar || ''}
                /> : ''
            },
            {
                title: 'Updated At',
                dataIndex: 'updatedAt',
                key: 'updatedAt',
                width: 150,
                render: (text: string) => moment(text).format('DD MMM, YYYY hh:mm A')
            }
        ],
        actions: [
            { name: 'Add Requirement', actionType: 'Add' }
        ],
        combo: [
            {
                api: '/job-titles',
                name: 'jobTitle',
                idKey: '_id',
                render: (item) => item.name
            },
            {
                api: '/accounts',
                name: 'account',
                idKey: '_id',
                render: (item) => item.businessName
            },
            account && {
                api: `/contacts?business=${account}`,
                name: 'contact',
                rerender: [account],
                idKey: '_id',
                render: (item) => [item.firstName, item.lastName].filter(Boolean).join(' ')
            },
        ].filter(Boolean) as ComboConfig[],
    }), [account]);

    return (
        <MasterComponent config={config} />
    );
};

export default Requirements;